import React, { useEffect, useRef, useState } from 'react';
import Select, { components } from 'react-select';
import { useField } from 'formik';

const SELECT_ALL_VALUE = '__SELECT_ALL__';

const flattenOptions = (listOptions = []) => {
  return listOptions.flatMap((option) => {
    if (Array.isArray(option?.options)) {
      return option.options;
    }
    return [option];
  }).filter(Boolean);
};

const groupOptionsMapFromList = (listOptions = []) => {
  return listOptions.reduce((acc, option) => {
    if (Array.isArray(option?.options) && option.label) {
      acc[String(option.label)] = option.options.map((item) => item.value);
    }
    return acc;
  }, {});
};

const CustomSelectComponents = {
  ValueContainer: ({ children, ...props }) => {
    const { getValue, hasValue } = props;
    const nbValues = getValue().length;

    return (
      <components.ValueContainer {...props}>
        <div className={`${hasValue ? props.isMulti && nbValues > 1 ? 'ocultarItems' : 'seleccionItems' : ""}`}>{children}</div>
        {hasValue && props.isMulti && nbValues > 1 &&
          `${nbValues} Items seleccionados`
        }
      </components.ValueContainer>
    );
  },

  Option: (props) => {
    const optionValue = props.data?.value ?? props.value;
    const optionLabel = props.data?.label ?? props.label;
    const optionName = props.selectProps?.name ?? props.name;
    const isTreeMode = Boolean(props.selectProps?.treeMode);
    const isSingleTreeMode = isTreeMode && !props.isMulti;
    const isSelectAllOption = Boolean(props.data?.isSelectAll);
    const isChecked = isSelectAllOption ? Boolean(props.selectProps?.isAllSelected) : props.isSelected;

    if (isSingleTreeMode) {
      return (
        <div>
          <components.Option {...props}>
            <input
              id={`checkbox-${optionName}${optionValue}`}
              type="checkbox"
              className="form-check-input"
              checked={isChecked}
              onChange={() => null}
            />{' '}
            <label htmlFor={`checkbox-${optionName}${optionValue}`} className="fs-6 selector__tree-option-label">{optionLabel}</label>
          </components.Option>
        </div>
      );
    }

    return (
      <div>
        <components.Option {...props}>
          <input
            id={`checkbox-${optionName}${optionValue}`}
            type="checkbox"
            className="form-check-input"
            checked={isChecked}
            onChange={() => null}
          />{' '}
          <label htmlFor={`checkbox-${optionName}${optionValue}`} className="fs-6">{optionLabel}</label>
        </components.Option>
      </div>
    );
  },

  Group: (props) => {
    const { children, data, Heading, headingProps } = props;
    const groupKey = String(data?.label ?? '');
    const expandedGroups = props.selectProps?.expandedGroups || {};
    const isExpanded = expandedGroups[groupKey] !== false;

    return (
      <div className="selector__group selector__group--tree">
        <Heading
          {...headingProps}
          data={data}
          selectProps={props.selectProps}
        >
          {data?.label}
        </Heading>
        {isExpanded && <div className="selector__group-list-tree">{children}</div>}
      </div>
    );
  },

  GroupHeading: (props) => {
    const groupKey = String(props?.data?.label ?? '');
    const expandedGroups = props.selectProps?.expandedGroups || {};
    const isExpanded = expandedGroups[groupKey] !== false;
    const isMulti = Boolean(props.selectProps?.isMulti ?? props?.isMulti);
    const groupValues = props.selectProps?.groupOptionsMap?.[groupKey] || [];
    const selectedValueSet = props.selectProps?.selectedValueSet || new Set();
    const selectedCount = groupValues.filter((value) => selectedValueSet.has(value)).length;
    const isChecked = groupValues.length > 0 && selectedCount === groupValues.length;
    const isIndeterminate = selectedCount > 0 && selectedCount < groupValues.length;

    const toggleGroup = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (props.selectProps?.onToggleGroup) {
        props.selectProps.onToggleGroup(groupKey);
      }
    };

    const toggleGroupSelection = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (props.selectProps?.onToggleGroupSelection) {
        props.selectProps.onToggleGroupSelection(groupKey);
      }
    };

    return (
      <div
        className={`selector__group-heading-tree ${isExpanded ? 'is-open' : ''}`}
        onMouseDown={toggleGroup}
      >
        <span className={`selector__group-caret ${isExpanded ? 'is-open' : ''}`}>▸</span>
        {isMulti && (
          <input
            type="checkbox"
            className="form-check-input selector__group-checkbox"
            checked={isChecked}
            ref={(input) => {
              if (input) {
                input.indeterminate = isIndeterminate;
              }
            }}
            onMouseDown={toggleGroupSelection}
            onChange={() => null}
          />
        )}
        <span>{props.children}</span>
      </div>
    );
  }
};

const SelectCheckField = ({ label, ...props }) => {
  const selectRef = useRef(null);
  const { options, name, isMulti, disabled, helpers = '', fijo = null, treeMode = false } = props;
  const [field, meta] = useField(props);
  const normalizedOptions = Array.isArray(options) ? options : [];
  const [expandedGroups, setExpandedGroups] = useState({});
  const groupOptionsMap = groupOptionsMapFromList(normalizedOptions);
  const flatOptions = flattenOptions(normalizedOptions).filter((item) => item.value !== SELECT_ALL_VALUE);
  const selectedValues = Array.isArray(field.value) ? field.value : [];
  const selectedValueSet = new Set(selectedValues);
  const enableSelectAll = isMulti && props.allowSelectAll !== false;
  const isAllSelected = enableSelectAll && flatOptions.length > 0 && selectedValues.length === flatOptions.length;
  const optionsWithSelectAll = enableSelectAll
    ? [{ value: SELECT_ALL_VALUE, label: 'Seleccionar todo', isSelectAll: true }, ...normalizedOptions]
    : normalizedOptions;

  useEffect(() => {
    if (!treeMode) {
      return;
    }

    const nextExpandedState = normalizedOptions.reduce((acc, option) => {
      if (Array.isArray(option?.options) && option.label) {
        acc[String(option.label)] = false;
      }
      return acc;
    }, {});

    setExpandedGroups(nextExpandedState);
  }, [treeMode, normalizedOptions]);

  const onToggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !(prev[groupKey] !== false)
    }));
  };

  const onToggleGroupSelection = (groupKey) => {
    const groupValues = groupOptionsMap[groupKey] || [];
    const currentSelection = Array.isArray(field.value) ? field.value : [];
    const allSelectedInGroup = groupValues.length > 0 && groupValues.every((value) => currentSelection.includes(value));

    const nextSelection = allSelectedInGroup
      ? currentSelection.filter((value) => !groupValues.includes(value))
      : Array.from(new Set([...currentSelection, ...groupValues]));

    props.onChange(name, nextSelection.length === 0 ? '' : nextSelection);
  };

  const selectComponents = treeMode
    ? {
      ...CustomSelectComponents,
      Option: CustomSelectComponents.Option,
      Group: CustomSelectComponents.Group,
      GroupHeading: CustomSelectComponents.GroupHeading
    }
    : {
      ...CustomSelectComponents,
      Group: components.Group,
      GroupHeading: components.GroupHeading
    };
  
  const handleChange = (val) => {
    if (Array.isArray(val)) {
      const hasSelectAll = val.some((item) => item.value === SELECT_ALL_VALUE);

      if (hasSelectAll) {
        props.onChange(name, isAllSelected ? '' : flatOptions.map((item) => item.value));
        return;
      }

      props.onChange(name, val.length === 0 ? '' : val.map((item) => item.value));
      return;
    }

    props.onChange(name, val ? val.value : '');
  };

  const handleBlur = () => {
    props.onBlur(name, true);
  };

  const defaultValue = (listOptions) => {
    const flattened = flattenOptions(listOptions || []);
    if (Array.isArray(field.value)) {
      return flattened.filter((option) => field.value.indexOf(option.value) >= 0);
    }
    return flattened.find((option) => option.value === field.value) || null;
  };

  return (
    <div className="form-floating">
      <div ref={selectRef} className={`simple-select ${helpers} ${meta.touched && meta.error ? 'is-invalid' : ''} ${fijo === true || fijo === null ? 'isMultiple' : ''}`}>
        {label && (
          <label htmlFor={`field-${name}`}>{label}</label>
        )}
        <Select
          {...props}
          {...field}
          classNamePrefix="selector"
          inputId={`field-${name}`}
          name={name}
          options={optionsWithSelectAll}
          value={defaultValue(normalizedOptions)}
          isMulti={isMulti}
          closeMenuOnSelect={!isMulti}
          hideSelectedOptions={false}
          components={selectComponents}
          isAllSelected={isAllSelected}
          treeMode={treeMode}
          expandedGroups={expandedGroups}
          selectedValueSet={selectedValueSet}
          groupOptionsMap={groupOptionsMap}
          onToggleGroup={onToggleGroup}
          onToggleGroupSelection={onToggleGroupSelection}
          onChange={handleChange}
          onBlur={handleBlur}
          isClearable={false}
          placeholder="Seleccione"
          isDisabled={disabled}
          menuPosition="absolute"
          isSearchable={false}
          noOptionsMessage={() => 'Sin opciones'}
        />
      </div>
      {meta.touched && meta.error &&
        <small className="message">{meta.error}</small>
      }
    </div>
  );
};

export default SelectCheckField;
